import { useCallback, useEffect, useState } from "react";
import { CheckSquare, Square, Moon, Trash2, Loader2, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useUrlState } from "@/lib/useUrlState";
import { LOG_TYPES, describe, fmtTime, photoUrl } from "./logTypes";
import { QuickLogDialog } from "./QuickLogDialog";
import { IncidentDialog } from "./IncidentDialog";

const QUICK = ["meal", "bottle", "nap", "diaper", "potty", "activity", "mood", "photo", "note", "medication", "incident"];

export function DailyLogPanel() {
  const [rooms, setRooms] = useState(null);
  const [roomId, setRoomId] = useUrlState("room", "");
  const [selected, setSelected] = useState([]);
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null); // type
  const [filterKid, setFilterKid] = useState("");
  const token = localStorage.getItem("vlog_token");

  const loadRooms = useCallback(async () => {
    const { data } = await api.get("/daycare/rooms");
    setRooms(data.rooms);
    if (!data.rooms.some((r) => r.id === roomId)) setRoomId(data.rooms[0]?.id || "");
  }, [roomId, setRoomId]);
  const loadLog = useCallback(async () => {
    if (!roomId) return;
    const { data } = await api.get("/daycare/log", { params: { class_id: roomId } });
    setItems(data.items);
  }, [roomId]);

  useEffect(() => { loadRooms(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadLog(); setSelected([]); setFilterKid(""); }, [loadLog]);

  const room = rooms?.find((r) => r.id === roomId);
  const kids = room?.children || [];
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectPresent = () => setSelected(kids.filter((k) => k.status === "in").map((k) => k.id));
  const openNaps = items.filter((i) => i.type === "nap" && !i.ended_at);

  const endNap = async (item) => {
    try {
      await api.patch(`/daycare/log/${item.id}`, { ended_at: new Date().toISOString() });
      loadLog();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const remove = async (item) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/daycare/log/${item.id}`);
    loadLog();
  };

  if (!rooms) return <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>;
  if (!rooms.length) return <div className="p-10 text-center text-sm text-slate-500" data-testid="daily-log-empty">No rooms assigned yet.</div>;

  const shown = filterKid ? items.filter((i) => i.student_id === filterKid) : items;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6" data-testid="daily-log-panel">
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {rooms.map((r) => (
            <button key={r.id} onClick={() => setRoomId(r.id)} data-testid={`room-tab-${r.id}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${r.id === roomId ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
              {r.name} <span className="opacity-60">· {r.present}/{r.children.length}</span>
            </button>
          ))}
          <button onClick={selectPresent} className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline" data-testid="select-present-btn"><Users className="h-3.5 w-3.5" /> Select everyone here</button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="children-grid">
          {kids.map((k) => {
            const on = selected.includes(k.id);
            const napping = openNaps.some((n) => n.student_id === k.id);
            return (
              <button key={k.id} onClick={() => toggle(k.id)} data-testid={`child-card-${k.id}`}
                className={`relative rounded-2xl border-2 p-3 text-left transition ${on ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <span className="absolute right-2 top-2 text-primary">{on ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4 text-slate-300" />}</span>
                <div className="flex items-center gap-3">
                  {k.photo_data_url ? <img src={k.photo_data_url} alt="" className="h-11 w-11 rounded-full object-cover" /> :
                    <span className="h-11 w-11 rounded-full bg-slate-100 text-slate-600 inline-flex items-center justify-center font-semibold">{k.full_name.split(" ").map((p) => p[0]).slice(0, 2).join("")}</span>}
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{k.full_name}</div>
                    <div className={`text-[11px] font-medium ${k.status === "in" ? "text-emerald-600" : "text-slate-400"}`}>{napping ? "Napping" : k.status === "in" ? "Here" : "Not here"}</div>
                    {k.allergies?.length > 0 && <div className="mt-0.5 truncate text-[10px] font-semibold text-red-600" title={k.allergies.join(", ")} data-testid={`allergy-badge-${k.id}`}>⚠ {k.allergies.join(", ")}</div>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-slate-500">{selected.length ? `Log for ${selected.length} selected` : "Select children, then tap an activity"}</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {QUICK.map((t) => {
              const m = LOG_TYPES[t];
              return (
                <button key={t} disabled={!selected.length || (t === "incident" && selected.length !== 1)} title={t === "incident" ? "Select exactly one child" : ""} onClick={() => setDialog(t)} data-testid={`quick-${t}`}
                  className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-xs font-medium transition disabled:opacity-40 hover:shadow ${m.cls}`}>
                  <m.Icon className="h-5 w-5" /> {m.label}
                </button>
              );
            })}
          </div>
          {openNaps.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="open-naps">
              {openNaps.map((n) => (
                <button key={n.id} onClick={() => endNap(n)} data-testid={`end-nap-${n.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700">
                  <Moon className="h-3.5 w-3.5" /> Wake {n.student_name.split(" ")[0]} · since {fmtTime(n.occurred_at)}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <aside className="rounded-2xl border border-slate-200 bg-white p-4 self-start" data-testid="daily-timeline">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-sm font-semibold text-slate-900">Today in {room?.name}</div>
          <select value={filterKid} onChange={(e) => setFilterKid(e.target.value)} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs" data-testid="timeline-filter">
            <option value="">All children</option>
            {kids.map((k) => <option key={k.id} value={k.id}>{k.full_name}</option>)}
          </select>
        </div>
        {shown.length === 0 && <div className="py-8 text-center text-xs text-slate-400">Nothing logged yet.</div>}
        <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
          {shown.map((i) => {
            const m = LOG_TYPES[i.type] || LOG_TYPES.note;
            return (
              <li key={i.id} className="group flex items-start gap-3 rounded-xl border border-slate-100 p-2.5" data-testid={`log-item-${i.id}`}>
                <span className={`h-8 w-8 shrink-0 rounded-lg border inline-flex items-center justify-center ${m.cls}`}><m.Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs text-slate-500"><span className="font-semibold text-slate-900">{i.student_name.split(" ")[0]}</span> · {m.label} · {fmtTime(i.occurred_at)}</div>
                  {(describe(i) || i.note) && <div className="text-sm text-slate-700">{describe(i)}{i.note && <span className="text-slate-500"> — {i.note}</span>}</div>}
                  {i.photo_id && <img src={photoUrl(i.photo_id, token)} alt="" className="mt-1.5 max-h-36 rounded-lg object-cover" />}
                </div>
                <button onClick={() => remove(i)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600" data-testid={`log-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button>
              </li>
            );
          })}
        </ul>
      </aside>

      <IncidentDialog open={dialog === "incident"} child={kids.find((k) => selected.includes(k.id))} onClose={() => setDialog(null)} onSaved={() => { loadLog(); }} />
      <QuickLogDialog open={!!dialog && dialog !== "incident"} type={dialog || "note"} children={kids.filter((k) => selected.includes(k.id))} onClose={() => setDialog(null)} onSaved={() => { loadLog(); loadRooms(); }} />
    </div>
  );
}
